import { useState, useMemo, useCallback } from 'react';
import type { Position } from '../types/mexc';
import { TrendingUp, TrendingDown, Settings, X, Shield, WifiOff, AlertTriangle, Clock, CheckCircle, AlertCircle, Pause, Search, ChevronUp, ChevronDown, ChevronsUpDown } from 'lucide-react';
import { PositionControls } from './PositionControls';
import { calculateSafePnL, calculatePnLPercentage, calculateUnrealizedPnL, getContractSize } from '../utils/pnlCalculations';
import { useTrailingStopLoss } from '../hooks/useTrailingStopLoss';
import { useCurrentPrices } from '../hooks/useCurrentPrices';
import { useContractInfo } from '../hooks/useMexcApi';
import { APIConnectionStatus, ConnectionStatusBadge } from './ConnectionStatus';
import { PositionPersistenceService } from '../services/positionPersistence';
import type { ConnectionStatus } from '../services/positionPersistence';
import { useMultiPositionTSLStatus } from '../hooks/useTSLStatus';

interface PositionsTableProps {
  positions: Position[];
  isLoading: boolean;
  onSelectPosition: (position: Position) => void;
  detailed?: boolean;
  connectionStatus?: ConnectionStatus;
  isUsingCache?: boolean;
}

type SortField = 'symbol' | 'side' | 'size' | 'entryPrice' | 'currentPrice' | 'pnl' | 'pnlPercent' | 'leverage';
type SortDirection = 'asc' | 'desc' | null;

export function PositionsTable({ positions, isLoading, onSelectPosition, detailed = false, connectionStatus, isUsingCache }: PositionsTableProps) {
  const [selectedPosition, setSelectedPosition] = useState<Position | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortField, setSortField] = useState<SortField | null>(null);
  const [sortDirection, setSortDirection] = useState<SortDirection>(null);
  const { getTSLConfig } = useTrailingStopLoss();
  const { getCurrentPrice } = useCurrentPrices();

  // Get TSL status for all positions
  const tslStatusMap = useMultiPositionTSLStatus(positions);

  // Helper function to render TSL status indicator
  const renderTSLStatus = (position: Position) => {
    const tslStatus = tslStatusMap[position.positionId];

    if (!tslStatus || !tslStatus.isEnabled) {
      return (
        <div className="flex items-center justify-center">
          <span className="text-gray-400 dark:text-gray-500 text-xs">—</span>
        </div>
      );
    }

    // Health status icon
    const getHealthIcon = () => {
      switch (tslStatus.healthStatus) {
        case 'healthy':
          return <CheckCircle className="w-3 h-3 text-green-500" />;
        case 'warning':
          return <AlertCircle className="w-3 h-3 text-yellow-500" />;
        case 'error':
          return <AlertCircle className="w-3 h-3 text-red-500" />;
        case 'inactive':
          return <Pause className="w-3 h-3 text-gray-400" />;
        default:
          return <Shield className="w-3 h-3 text-gray-400" />;
      }
    };

    // Status text color
    const getStatusColor = () => {
      if (!tslStatus.isActive) return 'text-gray-500 dark:text-gray-400';
      switch (tslStatus.healthStatus) {
        case 'healthy':
          return 'text-green-600 dark:text-green-400';
        case 'warning':
          return 'text-yellow-600 dark:text-yellow-400';
        case 'error':
          return 'text-red-600 dark:text-red-400';
        default:
          return 'text-gray-600 dark:text-gray-400';
      }
    };

    return (
      <div className="flex flex-col items-center space-y-1">
        {/* Status indicator with health icon */}
        <div className="flex items-center space-x-1">
          <div className="relative">
            <Shield className="w-4 h-4 text-blue-600 dark:text-blue-400" />
            {tslStatus.isActive && (
              <div className="absolute -top-0.5 -right-0.5">
                {getHealthIcon()}
              </div>
            )}
          </div>
          <div className="text-xs">
            <div className={`font-medium ${getStatusColor()}`}>
              {tslStatus.trailingDistance}{tslStatus.trailingType === 'percentage' ? '%' : '$'}
            </div>
          </div>
        </div>

        {/* Current trailing stop price */}
        {tslStatus.currentTrailingStopPrice && (
          <div className="text-xs text-gray-500 dark:text-gray-400">
            ${tslStatus.currentTrailingStopPrice.toFixed(4)}
          </div>
        )}

        {/* Last adjustment time */}
        {tslStatus.lastAdjustmentTime && (
          <div className="flex items-center space-x-1 text-xs text-gray-400">
            <Clock className="w-2 h-2" />
            <span>
              {new Date(tslStatus.lastAdjustmentTime).toLocaleTimeString([], {
                hour: '2-digit',
                minute: '2-digit'
              })}
            </span>
          </div>
        )}

        {/* Health message tooltip */}
        {tslStatus.healthMessage && (
          <div className="text-xs text-gray-400 truncate max-w-20" title={tslStatus.healthMessage}>
            {tslStatus.healthMessage}
          </div>
        )}
      </div>
    );
  };

  // Helper function to render TSL trigger details
  const renderTSLTriggers = (position: Position) => {
    const tslStatus = tslStatusMap[position.positionId];
    const tslConfig = getTSLConfig(position.positionId);
    const contractInfo = contractInfoMap[position.symbol];
    const priceScale = contractInfo?.priceScale ?? 4;
    const tickSize = contractInfo?.priceUnit ?? (1 / Math.pow(10, priceScale));
    const formatPrice = (v?: number | null) =>
      typeof v === 'number' ? v.toFixed(Math.min(6, Math.max(2, priceScale))) : '—';

    if (!tslStatus || !tslStatus.isEnabled || !tslConfig) {
      return <div className="text-xs text-gray-400 dark:text-gray-500 text-center">N/A</div>;
    }

    const isLong = position.positionType === 1;

    // Determine current stop (sell trigger) – prefer status value, else derive from watermark like engine
    const waterMark = isLong ? tslConfig.highWaterMark : tslConfig.lowWaterMark;
    let stopPrice: number | null = tslStatus.currentTrailingStopPrice || null;
    if (!stopPrice && waterMark) {
      if (tslConfig.trailingType === 'percentage') {
        const trailingAmount = waterMark * (tslConfig.trailingDistance / 100);
        stopPrice = isLong ? waterMark - trailingAmount : waterMark + trailingAmount;
      } else {
        stopPrice = isLong ? waterMark - tslConfig.trailingDistance : waterMark + tslConfig.trailingDistance;
      }
    }

    // If still no stop and no watermark, show Activation Trigger details
    if (!stopPrice && !waterMark && tslStatus.isEnabled) {
      const entry = position.openAvgPrice;
      const qty = position.holdVol;
      const contractSize = getContractSize(position.symbol, contractInfo);
      const threshold = (tslConfig.minProfitThreshold ?? 0) / 100;

      const activationPrice = isLong ? entry * (1 + threshold) : entry * (1 - threshold);

      // The initial stop is computed from the activation price as the first watermark
      let initialStop: number;
      if (tslConfig.trailingType === 'percentage') {
        const amt = activationPrice * (tslConfig.trailingDistance / 100);
        initialStop = isLong ? activationPrice - amt : activationPrice + amt;
      } else {
        initialStop = isLong ? activationPrice - tslConfig.trailingDistance : activationPrice + tslConfig.trailingDistance;
      }

      const potentialProfit = isLong
        ? (activationPrice - entry) * qty * contractSize
        : (entry - activationPrice) * qty * contractSize;

      const currentPrice = getCurrentPrice(position.symbol)?.price ?? tslStatus.currentPrice;
      const distanceToActivation = currentPrice ? ((currentPrice - activationPrice) / activationPrice) * 100 : null;

      return (
        <div className="flex flex-col items-center md:items-end text-xs leading-tight">
          <div className="flex gap-2">
            <span className="text-gray-500 dark:text-gray-400">Activation @:</span>
            <span className="font-medium text-gray-900 dark:text-white">${formatPrice(activationPrice)}</span>
          </div>
          <div className="flex gap-2">
            <span className="text-gray-500 dark:text-gray-400">Initial Stop:</span>
            <span className="text-gray-900 dark:text-white">${formatPrice(initialStop)}</span>
          </div>
          <div className="flex gap-2">
            <span className="text-gray-500 dark:text-gray-400">Potential Profit:</span>
            <span className="text-gray-900 dark:text-white">{formatCurrency(potentialProfit)}</span>
          </div>
          <div className="flex gap-2">
            <span className="text-gray-500 dark:text-gray-400">To Activate:</span>
            <span className="text-gray-900 dark:text-white">{distanceToActivation !== null ? formatPercentage(distanceToActivation) : '—'}</span>
          </div>
        </div>
      );
    }

    // Movement trigger: the next price level where stop would advance by at least 1 tick
    const d = tslConfig.trailingType === 'percentage' ? tslConfig.trailingDistance / 100 : undefined;
    let movementTriggerPrice: number | null = null;
    if (stopPrice !== null && stopPrice !== undefined) {
      if (tslConfig.trailingType === 'percentage' && typeof d === 'number') {
        movementTriggerPrice = isLong ? (stopPrice + tickSize) / (1 - d) : (stopPrice - tickSize) / (1 + d);
      } else {
        const dist = tslConfig.trailingDistance;
        movementTriggerPrice = isLong ? stopPrice + dist + tickSize : stopPrice - dist - tickSize;
      }
    }

    // Projected profit if stopped at current stop price
    const contractSize = getContractSize(position.symbol, contractInfo);
    const qty = position.holdVol;
    const entry = position.openAvgPrice;
    const projectedProfit = stopPrice !== null && stopPrice !== undefined
      ? (isLong
        ? (stopPrice - entry) * qty * contractSize
        : (entry - stopPrice) * qty * contractSize)
      : 0;

    // Percentage above trigger relative to trigger price
    const currentPrice = getCurrentPrice(position.symbol)?.price ?? tslStatus.currentPrice;
    const percentAbove = stopPrice !== null && stopPrice !== undefined && currentPrice !== undefined
      ? ((currentPrice - stopPrice) / stopPrice) * 100
      : null;

    const profitColor = projectedProfit >= 0
      ? 'text-success-600 dark:text-success-400'
      : 'text-danger-600 dark:text-danger-400';
    const percentColor = percentAbove !== null && percentAbove >= 0
      ? 'text-success-600 dark:text-success-400'
      : 'text-danger-600 dark:text-danger-400';

    return (
      <div className="flex flex-col items-center md:items-end text-xs leading-tight">
        <div className="flex gap-2">
          <span className="text-gray-500 dark:text-gray-400">Sell Trigger:</span>
          <span className="font-medium text-gray-900 dark:text-white">${formatPrice(stopPrice)}</span>
        </div>
        <div className="flex gap-2">
          <span className="text-gray-500 dark:text-gray-400">Trail Trigger:</span>
          <span className="text-gray-900 dark:text-white">${formatPrice(movementTriggerPrice)}</span>
        </div>
        <div className="flex gap-2">
          <span className="text-gray-500 dark:text-gray-400">Profit @SL:</span>
          <span className={`font-medium ${profitColor}`}>{formatCurrency(projectedProfit)}</span>
        </div>
        <div className="flex gap-2">
          <span className="text-gray-500 dark:text-gray-400">Above Trigger:</span>
          <span className={`font-medium ${percentColor}`}>{percentAbove !== null ? formatPercentage(percentAbove) : '—'}</span>
        </div>
        {tslStatus.lastAdjustmentTime && (
          <div className="flex gap-2 mt-1 pt-1 border-t border-gray-200 dark:border-gray-600">
            <span className="text-gray-500 dark:text-gray-400">Last trigger:</span>
            <span className="text-blue-600 dark:text-blue-400 font-medium">{formatRelativeTime(tslStatus.lastAdjustmentTime)}</span>
          </div>
        )}
      </div>
    );
  };


  // Fetch contract info for all symbols to get accurate contract sizes
  const { data: contractInfoList } = useContractInfo();

  // Create a map of symbol to contract info for quick lookup
  const contractInfoMap = useMemo(() => {
    if (!contractInfoList) return {};
    // Handle both array and single object responses
    const contractArray = Array.isArray(contractInfoList) ? contractInfoList : [contractInfoList];
    return contractArray.reduce((acc, info) => {
      if (info && info.symbol) {
        acc[info.symbol] = info;
      }
      return acc;
    }, {} as Record<string, any>);
  }, [contractInfoList]);

  // Handle column sorting
  const handleSort = useCallback((field: SortField) => {
    if (sortField === field) {
      // Cycle through: asc -> desc -> null
      if (sortDirection === 'asc') {
        setSortDirection('desc');
      } else if (sortDirection === 'desc') {
        setSortField(null);
        setSortDirection(null);
      } else {
        setSortDirection('asc');
      }
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  }, [sortField, sortDirection]);

  // Get sort icon for column header
  const getSortIcon = (field: SortField) => {
    if (sortField !== field) {
      return <ChevronsUpDown className="w-3 h-3 ml-1 opacity-50" />;
    }
    if (sortDirection === 'asc') {
      return <ChevronUp className="w-3 h-3 ml-1" />;
    }
    return <ChevronDown className="w-3 h-3 ml-1" />;
  };

  // Filter and sort positions
  const filteredAndSortedPositions = useMemo(() => {
    let result = [...positions];

    // Apply search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase().trim();
      result = result.filter(p => p.symbol.toLowerCase().includes(query));
    }

    // Apply sorting
    if (sortField && sortDirection) {
      result.sort((a, b) => {
        let aValue: number | string;
        let bValue: number | string;

        switch (sortField) {
          case 'symbol':
            aValue = a.symbol;
            bValue = b.symbol;
            break;
          case 'side':
            aValue = a.positionType;
            bValue = b.positionType;
            break;
          case 'size':
            aValue = a.holdVol;
            bValue = b.holdVol;
            break;
          case 'entryPrice':
            aValue = a.openAvgPrice;
            bValue = b.openAvgPrice;
            break;
          case 'currentPrice':
            aValue = getCurrentPrice(a.symbol)?.price || 0;
            bValue = getCurrentPrice(b.symbol)?.price || 0;
            break;
          case 'pnl': {
            const aPrice = getCurrentPrice(a.symbol)?.price || null;
            const bPrice = getCurrentPrice(b.symbol)?.price || null;
            const aContractInfo = contractInfoMap[a.symbol];
            const bContractInfo = contractInfoMap[b.symbol];
            aValue = aPrice
              ? calculateUnrealizedPnL(a, aPrice, aContractInfo)
              : calculateSafePnL(a, undefined, aContractInfo);
            bValue = bPrice
              ? calculateUnrealizedPnL(b, bPrice, bContractInfo)
              : calculateSafePnL(b, undefined, bContractInfo);
            break;
          }
          case 'pnlPercent': {
            const aPrice = getCurrentPrice(a.symbol)?.price || null;
            const bPrice = getCurrentPrice(b.symbol)?.price || null;
            const aContractInfo = contractInfoMap[a.symbol];
            const bContractInfo = contractInfoMap[b.symbol];
            aValue = aPrice
              ? calculatePnLPercentage(a, aPrice, aContractInfo)
              : calculatePnLPercentage(a, undefined, aContractInfo);
            bValue = bPrice
              ? calculatePnLPercentage(b, bPrice, bContractInfo)
              : calculatePnLPercentage(b, undefined, bContractInfo);
            break;
          }
          case 'leverage':
            aValue = a.leverage;
            bValue = b.leverage;
            break;
          default:
            return 0;
        }

        if (typeof aValue === 'string' && typeof bValue === 'string') {
          const comparison = aValue.localeCompare(bValue);
          return sortDirection === 'asc' ? comparison : -comparison;
        }

        const comparison = (aValue as number) - (bValue as number);
        return sortDirection === 'asc' ? comparison : -comparison;
      });
    }

    return result;
  }, [positions, searchQuery, sortField, sortDirection, getCurrentPrice, contractInfoMap]);

  if (isLoading) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <div className="h-6 bg-gray-200 dark:bg-gray-700 rounded w-1/4 animate-pulse"></div>
        </div>
        <div className="p-6 space-y-4">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="h-16 bg-gray-200 dark:bg-gray-700 rounded animate-pulse"></div>
          ))}
        </div>
      </div>
    );
  }

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 6,
    }).format(value);
  };

  const formatPercentage = (value: number) => {
    return `${value >= 0 ? '+' : ''}${value.toFixed(2)}%`;
  };

  // Format relative time for position age display
  const formatRelativeTime = (timestamp: number): string => {
    const diff = Date.now() - timestamp;
    const seconds = Math.floor(diff / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);

    if (days > 0) return `${days}d ago`;
    if (hours > 0) return `${hours}h ago`;
    if (minutes > 0) return `${minutes}m ago`;
    return `${seconds}s ago`;
  };

  // Note: Using imported utility functions for P&L calculations
  // calculateSafePnL and calculatePnLPercentage are now imported from utils

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
      <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="flex items-center gap-3">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              Open Positions ({filteredAndSortedPositions.length}{searchQuery ? ` of ${positions.length}` : ''})
            </h3>
            {connectionStatus && (
              <ConnectionStatusBadge
                connectionStatus={connectionStatus}
                isUsingCache={isUsingCache}
              />
            )}
          </div>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search symbol..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9 pr-4 py-2 w-full sm:w-64 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>
        {connectionStatus && isUsingCache && (
          <div className="mt-3">
            <APIConnectionStatus
              connectionStatus={connectionStatus}
              isUsingCache={isUsingCache}
              className="text-sm"
            />
          </div>
        )}
      </div>

      {positions.length === 0 ? (
        <div className="p-12 text-center">
          {(() => {
            if (!connectionStatus) {
              // Fallback to original behavior if no connection status
              return (
                <>
                  <div className="w-16 h-16 bg-gray-100 dark:bg-gray-700 rounded-full flex items-center justify-center mx-auto mb-4">
                    <TrendingUp className="w-8 h-8 text-gray-400" />
                  </div>
                  <h4 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                    No Open Positions
                  </h4>
                  <p className="text-gray-500 dark:text-gray-400">
                    You don't have any open positions at the moment.
                  </p>
                </>
              );
            }

            const { showNoPositions, showConnectionIssues } = PositionPersistenceService.shouldShowNoPositions(
              positions,
              connectionStatus
            );

            if (showConnectionIssues) {
              return (
                <>
                  <div className="w-16 h-16 bg-red-100 dark:bg-red-900/20 rounded-full flex items-center justify-center mx-auto mb-4">
                    <WifiOff className="w-8 h-8 text-red-500" />
                  </div>
                  <h4 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                    Connection Issues
                  </h4>
                  <p className="text-gray-500 dark:text-gray-400 mb-4">
                    Unable to fetch position data from MEXC API.
                  </p>
                  {connectionStatus.lastError && (
                    <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3 text-sm">
                      <div className="flex items-center space-x-2 text-red-700 dark:text-red-300">
                        <AlertTriangle className="w-4 h-4" />
                        <span className="font-medium">Error {connectionStatus.lastError.code}</span>
                      </div>
                      <p className="text-red-600 dark:text-red-400 mt-1">
                        {connectionStatus.lastError.message}
                      </p>
                    </div>
                  )}
                </>
              );
            }

            if (showNoPositions) {
              return (
                <>
                  <div className="w-16 h-16 bg-gray-100 dark:bg-gray-700 rounded-full flex items-center justify-center mx-auto mb-4">
                    <TrendingUp className="w-8 h-8 text-gray-400" />
                  </div>
                  <h4 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                    No Open Positions
                  </h4>
                  <p className="text-gray-500 dark:text-gray-400">
                    You don't have any open positions at the moment.
                  </p>
                </>
              );
            }

            return null;
          })()}
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 dark:bg-gray-700">
              <tr>
                <th
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600 select-none"
                  onClick={() => handleSort('symbol')}
                >
                  <div className="flex items-center">
                    Symbol
                    {getSortIcon('symbol')}
                  </div>
                </th>
                <th
                  className="px-6 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600 select-none"
                  onClick={() => handleSort('side')}
                >
                  <div className="flex items-center justify-center">
                    Side
                    {getSortIcon('side')}
                  </div>
                </th>
                <th
                  className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600 select-none"
                  onClick={() => handleSort('size')}
                >
                  <div className="flex items-center justify-end">
                    Size
                    {getSortIcon('size')}
                  </div>
                </th>
                <th
                  className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600 select-none"
                  onClick={() => handleSort('entryPrice')}
                >
                  <div className="flex items-center justify-end">
                    Entry Price
                    {getSortIcon('entryPrice')}
                  </div>
                </th>
                <th
                  className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600 select-none"
                  onClick={() => handleSort('currentPrice')}
                >
                  <div className="flex items-center justify-end">
                    Current Price
                    {getSortIcon('currentPrice')}
                  </div>
                </th>
                <th
                  className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600 select-none"
                  onClick={() => handleSort('pnl')}
                >
                  <div className="flex items-center justify-end">
                    PnL
                    {getSortIcon('pnl')}
                  </div>
                </th>
                <th
                  className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600 select-none"
                  onClick={() => handleSort('leverage')}
                >
                  <div className="flex items-center justify-end">
                    Leverage
                    {getSortIcon('leverage')}
                  </div>
                </th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Opened
                </th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  <div className="flex items-center justify-center space-x-1">
                    <Shield className="w-3 h-3" />
                    <span>TSL Status</span>
                  </div>
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider whitespace-nowrap">
                  TSL Triggers
                </th>
                {detailed && (
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Actions
                  </th>
                )}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {filteredAndSortedPositions.map((position) => {
                // Get current market price
                const currentPriceData = getCurrentPrice(position.symbol);
                const currentPrice = currentPriceData?.price || null;

                // Calculate P&L using current price if available, otherwise use safe fallback
                // Using updated MEXC formula with accurate contract size
                const contractInfo = contractInfoMap[position.symbol];
                const pnl = currentPrice
                  ? calculateUnrealizedPnL(position, currentPrice, contractInfo)
                  : calculateSafePnL(position, undefined, contractInfo);
                const pnlPercentage = currentPrice
                  ? calculatePnLPercentage(position, currentPrice, contractInfo)
                  : calculatePnLPercentage(position, undefined, contractInfo);

                // Debug logging for P&L calculations (can be removed later)
                if (currentPrice && (position.symbol === 'SOL_USDT' || position.symbol === 'BTC_USDT')) {
                  // Use the imported getContractSize function to show actual contract size used
                  const contractSize = getContractSize(position.symbol);
                  const marginAmount = (position.openAvgPrice * position.holdVol * contractSize) / position.leverage;

                  console.log(`P&L Debug for ${position.symbol}:`, {
                    symbol: position.symbol,
                    positionType: position.positionType === 1 ? 'Long' : 'Short',
                    entryPrice: position.openAvgPrice,
                    currentPrice,
                    holdVol: position.holdVol,
                    leverage: position.leverage,
                    contractSize: contractSize,
                    marginAmount: marginAmount,
                    calculatedPnL: pnl,
                    calculatedPercentage: pnlPercentage,
                    pnlFormula: position.positionType === 1
                      ? `(${currentPrice} - ${position.openAvgPrice}) × ${position.holdVol} × ${contractSize} = ${pnl}`
                      : `(${position.openAvgPrice} - ${currentPrice}) × ${position.holdVol} × ${contractSize} = ${pnl}`,
                    percentageFormula: `(${pnl} / ${marginAmount}) × 100 = ${pnlPercentage}%`,
                    mexcExpectedValues: position.symbol === 'SOL_USDT'
                      ? 'MEXC shows: -$1.32 (-0.69%)'
                      : 'MEXC shows: -$0.12435 (-0.07%)'
                  });
                }

                const isLong = position.positionType === 1;
                // const tslConfig = getTSLConfig(position.positionId); // Unused, commented out

                // Determine price change indicator
                const priceChangeClass = currentPriceData?.source === 'polling' && currentPrice !== null
                  ? (currentPrice > position.openAvgPrice ? 'text-success-600 dark:text-success-400' : 'text-danger-600 dark:text-danger-400')
                  : 'text-gray-900 dark:text-white';

                return (
                  <tr
                    key={position.positionId}
                    className="hover:bg-gray-50 dark:hover:bg-gray-700/50 cursor-pointer"
                    onClick={() => onSelectPosition(position)}
                  >
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <span className="font-medium text-gray-900 dark:text-white">
                          {position.symbol}
                        </span>
                        <span className={`ml-2 px-2 py-1 text-xs rounded-full ${position.openType === 1
                          ? 'bg-blue-100 dark:bg-blue-900/20 text-blue-800 dark:text-blue-200'
                          : 'bg-purple-100 dark:bg-purple-900/20 text-purple-800 dark:text-purple-200'
                          }`}>
                          {position.openType === 1 ? 'Isolated' : 'Cross'}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-center">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${isLong
                        ? 'bg-success-100 dark:bg-success-900/20 text-success-800 dark:text-success-200'
                        : 'bg-danger-100 dark:bg-danger-900/20 text-danger-800 dark:text-danger-200'
                        }`}>
                        {isLong ? (
                          <>
                            <TrendingUp className="w-3 h-3 mr-1" />
                            Long
                          </>
                        ) : (
                          <>
                            <TrendingDown className="w-3 h-3 mr-1" />
                            Short
                          </>
                        )}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm text-gray-900 dark:text-white">
                      {position.holdVol.toLocaleString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm text-gray-900 dark:text-white">
                      {formatCurrency(position.openAvgPrice)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm">
                      <div className="flex flex-col items-end">
                        <span className={priceChangeClass}>
                          {currentPrice ? formatCurrency(currentPrice) : (
                            <span className="text-yellow-500 dark:text-yellow-400" title="Price unavailable - API may be unreachable">
                              —
                            </span>
                          )}
                        </span>
                        {currentPriceData && (
                          <span className="text-xs text-gray-500 dark:text-gray-400">
                            {currentPriceData.source === 'polling' ? '🔄 Live' : '📊 API'}
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm">
                      <div className={`${pnl >= 0
                        ? 'text-success-600 dark:text-success-400'
                        : 'text-danger-600 dark:text-danger-400'
                        }`}>
                        <div className="font-medium">
                          {formatCurrency(pnl)}
                        </div>
                        <div className="text-xs">
                          {formatPercentage(pnlPercentage)}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm text-gray-900 dark:text-white">
                      {position.leverage}x
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-center">
                      <div className="flex flex-col items-center">
                        <span className="text-xs text-gray-900 dark:text-white">
                          {new Date(position.createTime).toLocaleDateString()}
                        </span>
                        <span className="text-xs text-gray-500 dark:text-gray-400">
                          {formatRelativeTime(position.createTime)}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-center">
                      {renderTSLStatus(position)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right">
                      {renderTSLTriggers(position)}
                    </td>
                    {detailed && (
                      <td className="px-6 py-4 whitespace-nowrap text-center">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedPosition(position);
                          }}
                          className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                        >
                          <Settings className="w-4 h-4" />
                        </button>
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Position Controls Modal */}
      {selectedPosition && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-2 sm:p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-sm sm:max-w-2xl max-h-[95vh] sm:max-h-[90vh] flex flex-col">
            {/* Modal Header - Fixed */}
            <div className="flex items-center justify-between p-4 sm:p-6 border-b border-gray-200 dark:border-gray-700 flex-shrink-0">
              <h3 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-white truncate mr-4">
                Manage Position - {selectedPosition.symbol}
              </h3>
              <button
                onClick={() => setSelectedPosition(null)}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md transition-colors flex-shrink-0"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Modal Content - Scrollable */}
            <div className="flex-1 overflow-y-auto">
              <div className="p-4 sm:p-6">
                <PositionControls
                  position={selectedPosition}
                  onClose={() => setSelectedPosition(null)}
                />
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
